import { APP_ROUTES } from '../../../app/routePaths';
import type { EmployeeNotification } from '../api/employeeNotificationsApi';

/** System problems (PTP's or MMSW's schedule not updating, phone alerts not working): the only notices tinted red. */
const SYSTEM_KINDS = ['PTP_SCHEDULE_STALE', 'MMSW_SCHEDULE_STALE', 'PHONE_PUSH_DOWN'];

/**
 * Where a vessel notice opens: "MMSW schedule not updating" always opens the MMSW schedule page (as PTP's
 * opens PTP's; the server links it to Vessel changes); others their own link, else the Westports schedule
 * page for "please upload the Westports schedule", else Vessel changes.
 */
export const vesselNoticePath = ({ kind, linkPath }: Pick<EmployeeNotification, 'kind' | 'linkPath'>): string => {
  if (kind === 'MMSW_SCHEDULE_STALE') return APP_ROUTES.mmswSchedule;
  if (linkPath) return linkPath;
  return kind === 'WESTPORTS_UPLOAD_DUE' ? APP_ROUTES.westportsSchedule : APP_ROUTES.vesselChanges;
};

/** Notices listed before "Show all": the unread ones, at most this many. */
export const FIRST_NOTICES = 5;

export const isSystemNotice = ({ kind }: Pick<EmployeeNotification, 'kind'>): boolean => SYSTEM_KINDS.includes(kind);

/** The leader's escalation: "Not handled: …" or "Still not handled: …". */
export const isEscalationNotice = ({ title }: Pick<EmployeeNotification, 'title'>): boolean =>
  title.startsWith('Not handled:') || title.startsWith('Still not handled:');

/**
 * Leaves out an escalation notice while a team alert row for the same vessel is shown above it (the row
 * says the same). It stays in the notification history; the unread count stays the server's.
 */
export const withoutShownEscalations = (items: EmployeeNotification[], teamVesselNames: string[]): EmployeeNotification[] => {
  const names = teamVesselNames.map((name) => name.trim().toUpperCase()).filter(Boolean);
  if (names.length === 0) return items;
  return items.filter((item) => !(isEscalationNotice(item) && names.some((name) => item.title.toUpperCase().includes(name))));
};

/** Collapsed: the first unread notices only; every notice once "Show all" is on. */
export const noticesToShow = (items: EmployeeNotification[], showAll: boolean): EmployeeNotification[] =>
  showAll ? items : items.filter((item) => !item.read).slice(0, FIRST_NOTICES);
